import { apiGet } from './api.js';

/**
 * Pure + DOM-adjacent helpers backing the header food-tip strip
 * (see openspec/changes/header-food-tip/design.md D9). Split from the
 * rendering component (tip-strip.js) so the cache/dismiss/gate logic is
 * unit-testable without booting jsdom — mirrors the outbox-core.js split.
 */

const TIP_CACHE_KEY = 'lardr.tip';
const TIP_DISMISSED_KEY = 'lardr.tipDismissed';

/** Cached tip is reused for this long before a fresh fetch is attempted. */
export const TIP_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * Returns true when the cached entry is still fresh enough to reuse
 * (age < TIP_CACHE_TTL_MS).
 *
 * @param {{ tip: object, ts: number }|null} cached
 * @param {number} now  ms-since-epoch (injectable for tests)
 * @returns {boolean}
 */
export function isCacheFresh(cached, now) {
  if (!cached || typeof cached.ts !== 'number') return false;
  return now - cached.ts < TIP_CACHE_TTL_MS;
}

/**
 * Format a Date as a local YYYY-MM-DD string (no UTC shift — the dismiss
 * flag is meant to mean "today" in the user's own timezone).
 *
 * @param {Date} date
 * @returns {string}
 */
export function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * True when the tip should stay hidden for the given "now" — i.e. the
 * stored dismissed-date equals today's local date.
 *
 * @param {string|null} dismissedDate  Value of lardr.tipDismissed, or null
 * @param {Date} now
 * @returns {boolean}
 */
export function isDismissedToday(dismissedDate, now) {
  if (!dismissedDate) return false;
  return dismissedDate === toLocalDateString(now);
}

/**
 * Read the cached {tip, ts} entry from localStorage, or null when absent
 * or malformed.
 *
 * @returns {{ tip: object, ts: number }|null}
 */
function readCache() {
  let raw;
  try {
    raw = localStorage.getItem(TIP_CACHE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.tip) return parsed;
  } catch {
    // fall through
  }
  return null;
}

function writeCache(tip, now) {
  try {
    localStorage.setItem(TIP_CACHE_KEY, JSON.stringify({ tip, ts: now }));
  } catch {
    // localStorage unavailable (private mode, quota) — non-critical
  }
}

/**
 * Fetch the current tip, reusing an unexpired cache entry and falling back
 * to any cached copy on network failure. Returns null only when there is
 * neither a fresh fetch nor any cached tip to fall back on (e.g. first
 * load, offline, endpoint 404s — render nothing per design).
 *
 * @param {{ now?: number }} [opts]  now is injectable for tests (ms-since-epoch)
 * @returns {Promise<object|null>}
 */
export async function fetchTip({ now = Date.now() } = {}) {
  const cached = readCache();
  if (isCacheFresh(cached, now)) {
    return cached.tip;
  }

  try {
    const tip = await apiGet('/tips/current');
    if (tip) writeCache(tip, now);
    return tip || (cached ? cached.tip : null);
  } catch {
    return cached ? cached.tip : null;
  }
}

/**
 * Read today's dismiss gate: false when the tip should render, true when
 * it was already dismissed today.
 *
 * @param {{ now?: Date }} [opts]
 * @returns {boolean}
 */
export function isDismissed({ now = new Date() } = {}) {
  let stored;
  try {
    stored = localStorage.getItem(TIP_DISMISSED_KEY);
  } catch {
    return false;
  }
  return isDismissedToday(stored, now);
}

/**
 * Record today's dismiss so both surfaces stay hidden until the next
 * calendar day.
 *
 * @param {{ now?: Date }} [opts]
 */
export function dismissTip({ now = new Date() } = {}) {
  try {
    localStorage.setItem(TIP_DISMISSED_KEY, toLocalDateString(now));
  } catch {
    // non-critical
  }
}
